"use client";
import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams, usePathname } from 'next/navigation';
import { useSearch } from '../../stores/useSearchStore';
import { ProductGridSkeleton } from './LoadingSkeletons';
import productService from '../../services/api/productService';
import { apiService } from '../../services/api/apiClient';
import ItemCard from './ProductCard';
import FilterDrawer from './FilterDrawer';

const ProductListContent = ({ title, subtitle, contextType }) => {
  const { isSearching, toggleActiveFilter, activeFilters, updateAdvancedFilter, advancedFilters } = useSearch();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0); // Total number of products from API
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);
  const [dynamicHeading, setDynamicHeading] = useState('All Products');

  // Determine dynamic heading based on context
  const getDynamicHeading = useCallback(() => {
    // If title prop is provided, use it
    if (title) {
      return title;
    }

    // Determine context from URL path first (more reliable)
    const isCategoryPage = pathname?.includes('/categories/');
    const isSearchPage = pathname?.includes('/search');
    const isProductsPage = pathname === '/products' || pathname?.startsWith('/products');

    // Check URL search params for context
    const category = searchParams?.get('category');
    const place = searchParams?.get('place');
    const searchQuery = searchParams?.get('q') || searchParams?.get('search');
    const subcategory = searchParams?.get('subcategory');
    // Check subcategory from query params (prioritize over category)
    if (subcategory && subcategory.trim()) {
      const subcategoryLabel = subcategory
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${subcategoryLabel} Products`;
    }

    // Check category from URL path (for /categories/dining routes)
    if (isCategoryPage && pathname) {
      const categorySlug = pathname.split('/categories/')[1]?.split('/')[0];
      if (categorySlug) {
        const categoryLabel = categorySlug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return `${categoryLabel} Products`;
      }
    }

    // Check category from query params (for /products?category=dining routes)
    if (category && category.trim()) {
      const categoryLabel = category
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${categoryLabel} Products`;
    }

    // Default fallback for /products page
    if (isProductsPage) {
      return 'All Products';
    }

    // Final fallback
    return 'All Products';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, searchParams?.toString(), pathname]);

  // Update heading when URL params or pathname change
  useEffect(() => {
    const heading = getDynamicHeading();
    setDynamicHeading(heading);
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log('ProductList Heading Update:', {
        pathname,
        searchParams: searchParams ? Object.fromEntries(searchParams.entries()) : null,
        heading,
        category: searchParams?.get('category'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.toString(), pathname, title]);

  const getDynamicSubtitle = () => {
    if (subtitle) {
      return subtitle;
    }
    const count = typeof totalCount === 'number' ? totalCount : 0;
    return count > 0 ? `${count.toLocaleString()} results` : `${products?.length || 0} results`;
  };

  // Initial load of products
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Build filters from URL params - extract values to create stable dependencies
        const filters = { page: 1, limit: 12 };
        let category = searchParams?.get('category');
        let place = searchParams?.get('place');
        const subcategory = searchParams?.get('subcategory');
        const searchQuery = searchParams?.get('q') || searchParams?.get('search') || searchParams?.get('query');

        // Extract category from pathname if provided (for /categories/[slug] routes)
        if (!category && pathname?.includes('/categories/')) {
          category = pathname.split('/categories/')[1]?.split('/')[0];
        }

        // Normalize category to lowercase
        if (category) {
          category = category.toLowerCase().trim();
        }

        if (category) {
          filters.category = category;
        }
        if (subcategory) {
          filters.subcategory = subcategory;
        }
        if (place) {
          filters.place = place;
        }

        // Check if we need to use the powerful POST /search endpoint
        // Use it for search queries, price filters, color filters, etc.
        const hasPriceFilters = (advancedFilters.minPrice !== '' && advancedFilters.minPrice !== null && advancedFilters.minPrice !== undefined && advancedFilters.minPrice !== 'null') ||
          (advancedFilters.maxPrice !== '' && advancedFilters.maxPrice !== null && advancedFilters.maxPrice !== undefined && advancedFilters.maxPrice !== 'null');
        
        const hasSearchQuery = searchQuery && searchQuery.trim() !== '';
        
        // Map sortBy to sort parameter (server expects 'sort' not 'sortBy')
        const sortMap = {
          'price_asc': 'price-low',
          'price_desc': 'price-high',
          'rating': 'rating',
          'newest': 'name', // Server doesn't have newest, use name as fallback
          'oldest': 'name',
          'relevance': null, // No sort parameter for relevance
        };

        let response;

        if (hasSearchQuery || hasPriceFilters || (advancedFilters.color && advancedFilters.color.trim() !== '')) {
          // Use POST /search endpoint for complex filtering and search
          const searchParamsData = {
            query: hasSearchQuery ? searchQuery.trim() : '*',
            limit: filters.limit || 12,
            offset: (filters.page - 1) * (filters.limit || 12),
          };

          if (filters.category) {
            searchParamsData.category = filters.category;
          }
          if (filters.subcategory) {
            searchParamsData.subcategory = filters.subcategory;
          }

          // Map sortBy to sort
          if (advancedFilters.sortBy && advancedFilters.sortBy !== 'relevance') {
            searchParamsData.sort = sortMap[advancedFilters.sortBy] || null;
          }

          // Add price filters
          if (advancedFilters.minPrice !== '' && advancedFilters.minPrice !== null && advancedFilters.minPrice !== undefined && advancedFilters.minPrice !== 'null') {
            const minPriceNum = Number(advancedFilters.minPrice);
            if (!isNaN(minPriceNum) && minPriceNum >= 0) {
              searchParamsData.minPrice = minPriceNum;
            }
          }
          if (advancedFilters.maxPrice !== '' && advancedFilters.maxPrice !== null && advancedFilters.maxPrice !== undefined && advancedFilters.maxPrice !== 'null') {
            const maxPriceNum = Number(advancedFilters.maxPrice);
            if (!isNaN(maxPriceNum) && maxPriceNum >= 0) {
              searchParamsData.maxPrice = maxPriceNum;
            }
          }

          // Add color filter
          if (advancedFilters.color && advancedFilters.color.trim() !== '') {
            searchParamsData.color = advancedFilters.color.trim();
          }

          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('ProductList: Using POST /search with params:', searchParamsData);
          }

          response = await apiService.products.search(searchParamsData.query, searchParamsData);
        } else {
          // Use GET /products endpoint for simple filtering
          // Map sortBy to sort
          if (advancedFilters.sortBy && advancedFilters.sortBy !== 'relevance') {
            filters.sort = sortMap[advancedFilters.sortBy] || null;
          }

          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('ProductList: Using GET /products with filters:', filters);
          }

          response = await productService.getAllProducts(filters);
        }

        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('ProductList: API response:', {
            success: response.success,
            productsCount: response.data?.products?.length || 0,
            error: response.error,
          });
        }

        if (response.success) {
          const fetchedProducts = response.data?.products || [];
          setProducts(fetchedProducts);

          // Handle pagination from API response
          if (response.data?.pagination) {
            const pagination = response.data.pagination;
            setTotalPages(pagination.totalPages || 1);
            setTotalCount(pagination.total || fetchedProducts.length);
            setHasMore(pagination.page < pagination.totalPages);
          } else if (response.data?.total !== undefined) {
            const total = response.data.total;
            const limit = filters.limit || 12;
            const currentPage = filters.page || 1;
            const totalPages = Math.ceil(total / limit);
            setTotalPages(totalPages);
            setTotalCount(total);
            setHasMore(currentPage < totalPages);
          } else {
            setTotalPages(1);
            setTotalCount(fetchedProducts.length);
            setHasMore(fetchedProducts.length === 12);
          }
        } else {
          setError(response.error || 'Failed to load products');
          setProducts([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err?.message || 'Failed to load products. Please try again later.');
        setProducts([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.toString(), pathname, advancedFilters.sortBy, advancedFilters.minPrice, advancedFilters.maxPrice, advancedFilters.color, advancedFilters.category, advancedFilters.subcategory, advancedFilters.minRating, advancedFilters.inStock, advancedFilters.tags, advancedFilters.size, advancedFilters.featured, advancedFilters.onSale, advancedFilters.topSeller]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || page >= totalPages) return;

    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;

      // Build filters from URL params to maintain category/place/subcategory filtering
      const filters = { page: nextPage, limit: 12 };
      let category = searchParams?.get('category');
      let place = searchParams?.get('place');
      const subcategory = searchParams?.get('subcategory');
      const searchQuery = searchParams?.get('q') || searchParams?.get('search') || searchParams?.get('query');

      // Extract category from pathname if provided
      if (!category && pathname?.includes('/categories/')) {
        category = pathname.split('/categories/')[1]?.split('/')[0];
      }

      // Normalize category to lowercase
      if (category) {
        category = category.toLowerCase().trim();
      }

      if (category) {
        filters.category = category;
      }
      if (subcategory) {
        filters.subcategory = subcategory;
      }
      if (place) {
        filters.place = place;
      }

      // Check if we need to use POST /search endpoint
      const hasPriceFilters = (advancedFilters.minPrice !== '' && advancedFilters.minPrice !== null && advancedFilters.minPrice !== undefined && advancedFilters.minPrice !== 'null') ||
        (advancedFilters.maxPrice !== '' && advancedFilters.maxPrice !== null && advancedFilters.maxPrice !== undefined && advancedFilters.maxPrice !== 'null');
      
      const hasSearchQuery = searchQuery && searchQuery.trim() !== '';

      // Map sortBy to sort parameter (server expects 'sort' not 'sortBy')
      const sortMap = {
        'price_asc': 'price-low',
        'price_desc': 'price-high',
        'rating': 'rating',
        'newest': 'name',
        'oldest': 'name',
        'relevance': null,
      };

      let response;

      if (hasSearchQuery || hasPriceFilters || (advancedFilters.color && advancedFilters.color.trim() !== '')) {
        // Use POST /search endpoint
        const searchParamsData = {
          query: hasSearchQuery ? searchQuery.trim() : '*',
          limit: filters.limit || 12,
          offset: (nextPage - 1) * (filters.limit || 12),
        };

        if (filters.category) {
          searchParamsData.category = filters.category;
        }
        if (filters.subcategory) {
          searchParamsData.subcategory = filters.subcategory;
        }

        // Map sortBy to sort
        if (advancedFilters.sortBy && advancedFilters.sortBy !== 'relevance') {
          searchParamsData.sort = sortMap[advancedFilters.sortBy] || null;
        }

        // Add price filters
        if (advancedFilters.minPrice !== '' && advancedFilters.minPrice !== null && advancedFilters.minPrice !== undefined && advancedFilters.minPrice !== 'null') {
          const minPriceNum = Number(advancedFilters.minPrice);
          if (!isNaN(minPriceNum) && minPriceNum >= 0) {
            searchParamsData.minPrice = minPriceNum;
          }
        }
        if (advancedFilters.maxPrice !== '' && advancedFilters.maxPrice !== null && advancedFilters.maxPrice !== undefined && advancedFilters.maxPrice !== 'null') {
          const maxPriceNum = Number(advancedFilters.maxPrice);
          if (!isNaN(maxPriceNum) && maxPriceNum >= 0) {
            searchParamsData.maxPrice = maxPriceNum;
          }
        }

        // Add color filter
        if (advancedFilters.color && advancedFilters.color.trim() !== '') {
          searchParamsData.color = advancedFilters.color.trim();
        }

        response = await apiService.products.search(searchParamsData.query, searchParamsData);
      } else {
        // Use GET /products endpoint
        if (advancedFilters.sortBy && advancedFilters.sortBy !== 'relevance') {
          filters.sort = sortMap[advancedFilters.sortBy] || null;
        }

        response = await productService.getAllProducts(filters);
      }

      if (response.success) {
        const newItems = response.data?.products || [];
        if (newItems.length > 0) {
          setProducts(prev => [...prev, ...newItems]);
          setPage(nextPage);

          if (response.data?.pagination) {
            const pagination = response.data.pagination;
            setTotalPages(pagination.totalPages || nextPage);
            setHasMore(pagination.page < pagination.totalPages);
          } else if (response.data?.total !== undefined) {
            const total = response.data.total;
            const limit = filters.limit || 12;
            const totalPages = Math.ceil(total / limit);
            setTotalPages(totalPages);
            setHasMore(nextPage < totalPages);
          } else {
            setHasMore(newItems.length === 12);
          }
        } else {
          setHasMore(false);
        }
      } else {
        setError(response.error || 'Failed to load more products');
      }
    } catch (err) {
      console.error('Error loading more products:', err);
      setError(err?.message || 'Failed to load more products. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages, hasMore, isLoadingMore, searchParams?.toString(), pathname, advancedFilters.sortBy, advancedFilters.minPrice, advancedFilters.maxPrice, advancedFilters.color, advancedFilters.category, advancedFilters.minRating, advancedFilters.inStock, advancedFilters.tags, advancedFilters.size, advancedFilters.featured, advancedFilters.onSale, advancedFilters.topSeller, advancedFilters.subcategory]);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' } // Trigger when 100px before reaching the element
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, isLoading, loadMore]);


  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [advancedFilters.sortBy, advancedFilters.minPrice, advancedFilters.maxPrice, advancedFilters.color, advancedFilters.category, advancedFilters.minRating, advancedFilters.inStock, advancedFilters.tags, advancedFilters.size, advancedFilters.featured, advancedFilters.onSale, advancedFilters.topSeller]);

  // Count active filters for badge
  const getActiveFilterCount = () => {
    let count = 0;
    if (advancedFilters.sortBy && advancedFilters.sortBy !== 'relevance') count++;
    if (advancedFilters.minPrice || advancedFilters.maxPrice) count++;
    if (advancedFilters.color) count++;
    if (advancedFilters.category) count++;
    if (advancedFilters.minRating > 0) count++;
    if (advancedFilters.inStock !== null) count++;
    if (advancedFilters.tags && advancedFilters.tags.length > 0) count++;
    if (advancedFilters.size) count++;
    if (advancedFilters.featured !== null) count++;
    if (advancedFilters.onSale !== null) count++;
    if (advancedFilters.topSeller !== null) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Show loading skeleton while searching or initial load
  if (isSearching || isLoading) {
    return <ProductGridSkeleton count={12} />;
  }

  // Show error state
  if (error && products.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center px-4">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Failed to Load Products</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                setError(null);
                setIsLoading(true);
                window.location.reload();
              }}
              className="w-full sm:w-auto px-8 py-3 bg-[#0B4866] text-white rounded-lg hover:bg-[#0a3d54] transition-colors font-medium shadow-md"
            >
              Try Again
            </button>
            <Link
              href="/products"
              className="w-full sm:w-auto px-8 py-3 border border-[#0B4866] text-[#0B4866] rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Explore All Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-normal text-gray-800 mb-2">
            {(() => {
              const heading = dynamicHeading || getDynamicHeading();
              if (typeof heading === 'string' && heading.includes('Search results for "')) {
                const query = heading.replace('Search results for "', '').replace('"', '');
                return (
                  <>
                    Search results for <span className="font-medium">"{query}"</span>
                  </>
                );
              }
              return <span className="font-medium">{String(heading || '')}</span>;
            })()}
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            {getDynamicSubtitle()}
          </p>
        </div>

        {/* Filter Button */}
        <div className="flex justify-end mb-6 md:mb-8">
          <button
            onClick={() => toggleActiveFilter('all')}
            className="relative px-4 md:px-6 py-2 border border-gray-300 rounded-md text-sm md:text-base font-medium text-[#0B4866] hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap transition-colors"
          >
            <SlidersHorizontal size={18} className="md:w-5 md:h-5" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#0B4866] text-xs font-semibold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Drawer */}
        <FilterDrawer />

        {/* Error message if there's an error but products are shown */}
        {error && products.length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            {error}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && products.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="mb-6">
              <svg className="w-20 h-20 text-gray-200 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h2 className="text-xl font-medium text-gray-800 mb-2">
                {(() => {
                  const subcategory = searchParams?.get('subcategory');
                  if (subcategory) {
                    const label = subcategory
                      .split('-')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');
                    return `No products found in ${label}`;
                  }
                  return "No Products Found";
                })()}
              </h2>
              <p className="text-gray-500 max-w-md mx-auto mb-8">
                {(() => {
                  const subcategory = searchParams?.get('subcategory');
                  if (subcategory) {
                    const label = subcategory
                      .split('-')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');
                    return `There are currently no products available in the ${label.toLowerCase()} sub-category. Please check back later or explore our other collections.`;
                  }
                  return "We couldn't find any products that match your current search or filters. Try adjusting your selections or explore our full collection.";
                })()}
              </p>
            </div>
            <Link
              href="/products"
              className="inline-flex items-center px-8 py-3 bg-[#0B4866] text-white rounded-lg hover:bg-[#094058] transition-all transform hover:scale-[1.02] shadow-md active:scale-95 font-medium"
            >
              Explore All Products
            </Link>
          </div>
        )}

        {/* Products Grid - Vertical on mobile, grid on desktop */}
        {products.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {products.map((product) => (
              <ItemCard key={product.id} item={product} variant="grid" />
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel and loading indicator */}
        {products.length > 0 && (
          <div ref={observerTarget} className="mt-8 md:mt-12">
            {isLoadingMore && (
              <div className="flex justify-center items-center py-8">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4866]"></div>
                  <p className="text-sm text-gray-600">Loading more products...</p>
                </div>
              </div>
            )}
            {!hasMore && !isLoadingMore && (
              <div className="text-center text-gray-500 text-sm py-8">
                You've reached the end of the product list.
              </div>
            )}
          </div>
        )}

        <div className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 py-8 md:py-12 border-t border-b border-gray-200">
          <Link href="/search?category=kitchen" className="text-center hover:opacity-80 transition-opacity cursor-pointer">
            <div className="text-[#0B4866] mb-2 md:mb-3 flex justify-center">
              <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">KITCHEN</h3>
            <p className="text-xs md:text-sm text-gray-600"></p>
          </Link>
          <Link href="/search?category=lounges" className="text-center hover:opacity-80 transition-opacity cursor-pointer">
            <div className="text-[#0B4866] mb-2 md:mb-3 flex justify-center">
              <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h18M9 3v2m6-2v2M5 9h14m-7 4h.01M8 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">LIVING</h3>
            <p className="text-xs md:text-sm text-gray-600">Lounges</p>
          </Link>
          <Link href="/search?category=dining" className="text-center hover:opacity-80 transition-opacity cursor-pointer">
            <div className="text-[#0B4866] mb-2 md:mb-3 flex justify-center">
              <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">DINING</h3>
            <p className="text-xs md:text-sm text-gray-600">Tables</p>
          </Link>
          <Link href="/search?category=bedroom" className="text-center hover:opacity-80 transition-opacity cursor-pointer">
            <div className="text-[#0B4866] mb-2 md:mb-3 flex justify-center">
              <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">BEDROOM</h3>
            <p className="text-xs md:text-sm text-gray-600">Beds</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

const ProductListingPage = (props) => {
  return (
    <Suspense fallback={<ProductGridSkeleton count={12} />}>
      <ProductListContent {...props} />
    </Suspense>
  );
};

export default ProductListingPage;







